import {
  cancelOrganizationGpuJob,
  createOrganizationGpuJob,
  createOrganizationGpuTask,
  getOrganizationGpuJob,
  useAuthStore,
} from "@dutyon/apis";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  createRemoteGpuBatch,
  type RemoteGpuBatchPlan,
  type RemoteGpuBatchState,
  reviewRemoteGpuBatch,
} from "../services/remoteGpuBatch";

type Batch = ReturnType<typeof createRemoteGpuBatch>;
const empty = (): RemoteGpuBatchState => ({
  phase: "idle",
  startedAt: null,
  message: null,
  completedChunks: 0,
  totalChunks: 0,
  canRestore: true,
  preview: null,
  warningCount: null,
  staffingWarnings: [],
  warningsTruncated: false,
  boundary: null,
  history: null,
  generation: null,
  task: { phase: "idle", input: null, job: null, message: null, canRestore: true },
});
/** One controller per response editor. Other editors never share its receipt or apply its preview. */
export function useRemoteGpuBatch(
  context: string,
  source: string,
  locked: boolean,
  manualSource: { requestId: string; responseId: string },
  onComplete?: (preview: NonNullable<RemoteGpuBatchState["preview"]>) => void,
) {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const { requestId, responseId } = manualSource;
  const owner = useAuthStore((s) => s.user?.sub);
  const token = useAuthStore((s) => s.token);
  const role = useAuthStore((s) => s.user?.role);
  const live = useRef({ context, source, locked, requestId, responseId });
  live.current = { context, source, locked, requestId, responseId };
  const scope = JSON.stringify([context, source, owner, token, role, requestId, responseId]);
  const [binding, setBinding] = useState<{ scope: string; batch: Batch } | null>(null);
  const [state, setState] = useState<{ scope: string; value: RemoteGpuBatchState } | null>(null);
  const isCurrent = useCallback(() => {
    const latest = useAuthStore.getState();
    return (
      !!owner &&
      !!token &&
      latest.user?.sub === owner &&
      latest.token === token &&
      latest.user?.role === role &&
      live.current.context === context &&
      live.current.source === source &&
      live.current.requestId === requestId &&
      live.current.responseId === responseId
    );
  }, [owner, token, role, context, source, requestId, responseId]);
  useEffect(() => {
    if (!owner || !token || role !== "SUPER_ADMIN") {
      setBinding(null);
      setState(null);
      return;
    }
    let storage: Storage | undefined;
    try {
      storage = window.sessionStorage;
    } catch {
      /* Report memory-only recovery in the view. */
    }
    const task = createOrganizationGpuTask({
      owner,
      app: "backoffice",
      context,
      storage,
      transport: {
        create: createOrganizationGpuJob,
        get: getOrganizationGpuJob,
        cancel: cancelOrganizationGpuJob,
      },
    });
    const batch = createRemoteGpuBatch({
      owner,
      context,
      source,
      manualSource: { requestId, responseId },
      storage,
      task,
      load: (id) => getOrganizationGpuJob(owner, id),
      isCurrent,
      canSubmit: () => !live.current.locked,
      onComplete: (preview) => onCompleteRef.current?.(preview),
      clearOnComplete: !!onCompleteRef.current,
      newKey: () => crypto.randomUUID(),
    });
    const update = () => setState({ scope, value: batch.getSnapshot() });
    const unsubscribe = batch.subscribe(update);
    setBinding({ scope, batch });
    update();
    void batch.refresh();
    const timer = setInterval(() => {
      const next = batch.getSnapshot();
      if (next.phase === "running" || next.task.phase === "watching") void batch.refresh();
    }, 3000);
    return () => {
      clearInterval(timer);
      unsubscribe();
      const latest = useAuthStore.getState();
      batch.dispose(latest.user?.sub !== owner || latest.user?.role !== role || !latest.token);
    };
    // `isCurrent` reads the latest refs; scope captures the initiating account and source.
  }, [context, source, owner, token, role, scope, isCurrent, requestId, responseId]);
  const batch = binding?.scope === scope && isCurrent() ? binding.batch : null;
  return {
    ...(state?.scope === scope && isCurrent() ? state.value : empty()),
    owner,
    ready: !!batch,
    isCurrent,
    start: (plan: RemoteGpuBatchPlan) => batch?.start(plan),
    resume: () => batch?.resume(),
    retryFailed: () => batch?.retryFailed(),
    cancel: () => batch?.cancel(),
    clear: () => batch?.clear(),
    review: () => (batch ? reviewRemoteGpuBatch(batch) : Promise.resolve(null)),
  };
}
